import * as App from "@timesheets/routes-app";

import { Guid, Id } from "@gemma/typescript-types";

import { ActionTree } from "vuex";
import { IState } from "../../IState";
import { IUser } from "./IUser";
import { IWebApplication } from "@timesheets/webapp-support";
import { IsApiResponse } from "@gemma/api-common";
import { Profiles } from "@timesheets/routes-api-common";
import Router from "vue-router";
import Vue from "vue";
import { WebApplicationConstants } from "../../../setup/WebApplicationConstants";
import { WireEntities } from "@timesheets/common";
import { getHeaderImageUrl } from "@/utils";

export const actions: ActionTree<IUser, IState> = {
  async loadProfile({ commit, dispatch, getters, state }, { webApplication, notify = false }: { webApplication: IWebApplication; notify?: boolean }): Promise<WireEntities.IProfile | undefined> {
    let currentRole = state.currentRole;
    commit("clearRoles");
    const profile = (await webApplication.api.get<WireEntities.IProfile>(Profiles.Me)) as WireEntities.IProfile;
    if (IsApiResponse(profile)) {
      profile.meta.errors.forEach(error => WebApplicationConstants.defaultLogger.warning(error));
      commit("setRoles", []);
      dispatch("setCurrentRole", { role: undefined, forceSet: true });
      commit("setLoggedOut", undefined, { root: true });
      return;
    }

    for (const role of profile.roles) {
      if (role.recruitmentAgency.settings.logos.square !== false) {
        role.recruitmentAgency.settings.logos.square = getHeaderImageUrl(role.recruitmentAgency.names.currentCanonical, true, this);
      }

      if (role.recruitmentAgency.settings.logos.rectangular !== false) {
        role.recruitmentAgency.settings.logos.rectangular = getHeaderImageUrl(role.recruitmentAgency.names.currentCanonical, true, this);
      }
    }

    commit("setRoles", profile.roles);

    state.roles.forEach(role => dispatch("setRoleEnabled", { role, webApplication, notify }));

    const forceSet = !!currentRole;

    if (!currentRole) {
      if (state.currentRole) {
        currentRole = state.currentRole;
      } else {
        const enabledRoles = getters.enabledRoles as WireEntities.Roles.IRecruitmentAgencyRole[];
        currentRole = enabledRoles[0];
      }
    }

    dispatch("setCurrentRole", { role: currentRole, forceSet });

    return profile;
  },
  setCurrentRole({ commit }, { role = undefined, forceSet = false }: { role?: string | WireEntities.Roles.IRecruitmentAgencyRole; forceSet: boolean } = { role: undefined, forceSet: false }) {
    commit("mutateCurrentRole", { role, forceSet });
  },
  setHourFormat({ commit }, { hourFormat, recruitmentAgency }: { hourFormat: WireEntities.RecruitmentAgency.IHourFormat; recruitmentAgency: WireEntities.Roles.IRecruitmentAgencyRole<unknown> | Id<string> }) {
    commit("mutateHourFormat", { hourFormat, recruitmentAgency });
  },
  setRoleEnabled({ getters, state, commit }, { role, router, isEnabled = role.isEnabled, notify = false }: { role: WireEntities.Roles.IRecruitmentAgencyRole; router: Router; isEnabled?: boolean; notify?: boolean }) {
    const existingRole = state.roles.find(r => r.id === role.id);
    if (existingRole) {
      const isEnabledBeforeChange = existingRole ? existingRole.isEnabled : false;

      commit("mutateRoleEnabled", { role: existingRole, isEnabled });

      if (notify) {
        if (isEnabledBeforeChange !== role.isEnabled) {
          Vue.prototype.$notify("general", `Your ${role.name || ""} role has been ${role.isEnabled ? "enabled" : "disabled"}.`);
        }
      }

      const enabledRoles = getters.enabledRoles as WireEntities.Roles.IRecruitmentAgencyRole[];
      if (state.currentRole) {
        if (!enabledRoles.some(er => er.id === state.currentRole!.id)) {
          commit("mutateCurrentRole", { role: undefined, forceSet: true });
        }
      } else if (enabledRoles.length > 0) {
        commit("mutateCurrentRole", { role: enabledRoles[0], forceSet: true });
      }

      if (isEnabledBeforeChange && !isEnabled && state.currentRole && state.currentRole.id === existingRole.id) {
        // The old currentRole was disabled and is set as the current role
        let path = enabledRoles.length === 0 ? App.Roles.Root : App.Roles.GetRole(enabledRoles[0]);
        router.push(path).catch(() => {});
      }
    }
  },
  setRoleHasContractors({ commit, state }, { role, hasContractors }: { role: WireEntities.Roles.IRecruitmentAgencyRole; hasContractors: boolean }) {
    const _role = state.roles.find(r => r.id === role.id);
    if (_role) {
      commit("mutateRoleHasContractors", { role, hasContractors });
    }
  },
  setSuperAdministrator({ commit, state }, { role, isSuperAdministrator }: { role: WireEntities.Roles.IRecruitmentAgencyRole; isSuperAdministrator: boolean }) {
    const recruitmentAgencyAdministrationRole = state.roles.find(r => r.id === role.id) as WireEntities.Roles.IRecruitmentAgencyAdministrationRole | undefined;
    if (recruitmentAgencyAdministrationRole) {
      commit("mutateSuperAdministrator", { recruitmentAgencyAdministrationRole, isSuperAdministrator });
    }
  },
  setIsBetaAccessOn({ commit, state }, { recruitmentAgencyAdministrationRoleId, isBetaAccessOn }: { recruitmentAgencyAdministrationRoleId: Guid; isBetaAccessOn: boolean }) {
    const recruitmentAgencyAdministrationRole = state.roles.find(r => r.id === recruitmentAgencyAdministrationRoleId) as WireEntities.Roles.IRecruitmentAgencyAdministrationRole | undefined;
    if (recruitmentAgencyAdministrationRole) {
      commit("mutateIsBetaAccessOn", { recruitmentAgencyAdministrationRole, isBetaAccessOn });
    }
  },
};
